import { type AdvanceReport, advanceWithReport, previewAbsence } from './step';
import {
  MAX_ID_LENGTH,
  SAVE_VERSION,
  type ImportResult,
  type SaveEnvelope,
  createSave,
  exportSave,
  importSave,
  strField,
} from './save';
import { type SimState } from './state';

/**
 * PERSISTENCE — the lifecycle a UI layer drives, and the only route into a save.
 *
 * WHY THIS FILE EXISTS. Round 1 built a save surface with NO PRODUCTION CALLER.
 * Nothing in the app imported the load path, so nothing had ever been forced to
 * decide which of two sibling loaders the game opens through, whether a repaired
 * save is checked before it is played, or what becomes of the notes a load
 * produces. Every save defect the round-1 critic found was a consequence of that
 * one absence: an unused surface has no wrong answers, because it never has to
 * pick one. This is the caller. It picks.
 *
 * THE LIFECYCLE, in the order a session performs it:
 *   1. READ the stored text, or start fresh if there is none.
 *   2. OPEN it through the one door in save.ts — migrate, normalise, repair —
 *      and keep the notes, because a player told "repaired" is owed the chance
 *      to be told what was repaired.
 *   3. ADVANCE on every frame the host offers, from a TIMESTAMP the host hands
 *      in, never from a callback count.
 *   4. AUTOSAVE when due, and immediately after anything the player did on
 *      purpose.
 *   5. EXPORT / IMPORT a string on demand.
 *
 * PURITY. This module obeys the same law as the rest of the lane: no DOM, no
 * browser global, no clock. Storage arrives as an injected port and time arrives
 * as a `nowMs` argument. The browser adapter — three one-line methods over the
 * Web Storage API — belongs to the UI layer, outside this directory, so the
 * simulation stays runnable in a test, a worker or a headless harness with no
 * shim at all.
 *
 * WRITING NEVER THROWS INTO THE GAME. A storage write fails for reasons that
 * have nothing to do with this game: a quota, a private window, a browser with
 * site data switched off. Every read and write is wrapped, a failure becomes a
 * NOTE rather than an exception, and play continues. Losing the ability to
 * autosave is bad; taking the running session down with it is worse.
 */

/**
 * The storage port. Three methods, all synchronous, all allowed to throw — the
 * session catches. A browser adapter reads and writes the Web Storage API; a
 * test adapter is a Map; a worker adapter is whatever that worker has.
 */
export interface SaveStorage {
  read(key: string): string | null;
  write(key: string, value: string): void;
  remove(key: string): void;
}

/** The storage-key family name. The version is appended, never typed in. */
const SAVE_KEY_STEM = 'reelin.save.v';

/**
 * THE SLOT CHAIN: this build's storage key first, then every older format's,
 * newest first.
 *
 * SIM-D3. The key read 'reelin.save.v1' as a literal while SAVE_VERSION read 1,
 * and nothing related them - so the comment here, "versioned in the key so a
 * format change cannot half-load", promised a mechanism that could not operate:
 * the format DID change in round 2 and the key did not move. It is derived now,
 * which makes the promise true, and the derivation is what forces the rest of
 * this: once the key moves with the version, a bump would orphan every existing
 * save unless the older slots are still read. So they are, in order, once, and
 * the save is rewritten into the current slot the moment it is opened.
 *
 * Note what this deliberately does NOT do: it never writes to an old slot. A
 * player who rolls a deploy back finds their old save exactly where they left
 * it, rather than finding it overwritten by a newer format their old build
 * half-understood.
 */
export function saveKeysFor(version: number): readonly string[] {
  const v = Number.isSafeInteger(version) && version >= 1 ? version : 1;
  const keys: string[] = [];
  for (let n = v; n >= 1; n--) keys.push(`${SAVE_KEY_STEM}${n}`);
  return keys;
}

/** Every slot this build reads, newest first. */
export const SAVE_KEYS: readonly string[] = saveKeysFor(SAVE_VERSION);

/** Where this build WRITES. Always the head of the chain. */
export const SAVE_KEY: string = SAVE_KEYS[0] ?? `${SAVE_KEY_STEM}${SAVE_VERSION}`;

/**
 * How often an autosave is written while playing. Wall-clock, not tick count:
 * a throttled background tab must not autosave less often in wall-clock terms
 * than a focused one, for the same reason progress is read from the clock.
 */
export const AUTOSAVE_INTERVAL_MS = 15_000;

export interface SessionOptions {
  readonly storage: SaveStorage;
  /** The host's clock, read once by the host and handed in. */
  readonly nowMs: number;
  /** Seed for a fresh run, when there is nothing stored. */
  readonly seed: number;
  /** One exact slot, for a caller that knows which one it wants. */
  readonly key?: string;
  /**
   * The slot chain to read, newest first. Defaults to SAVE_KEYS, which is
   * derived from SAVE_VERSION. A caller supplies it only to open a save written
   * at a version other than this build's, which is what a migration test does -
   * and is why the fallback is exercised today, with exactly one shipped
   * version, instead of first running on the day a bump makes it load-bearing.
   */
  readonly keys?: readonly string[];
  readonly autosaveIntervalMs?: number;
}

export interface SaveOutcome {
  readonly written: boolean;
  /** '' on success; otherwise why the write did not happen. */
  readonly note: string;
}

/**
 * A live session. Held by the UI layer for as long as the page is open.
 *
 * It is a small mutable box around an immutable state, which is deliberate: the
 * simulation stays a pure function of (seed, tick index), and exactly one object
 * in the whole program knows which state is the current one.
 */
export interface Session {
  /** What opening the save had to do. Empty means it opened clean. */
  readonly openNotes: readonly string[];
  /** The current simulation state. Immutable; safe to hand straight to a renderer. */
  state(): SimState;
  /** The current envelope, including the wardrobe and any forward fields. */
  envelope(): SaveEnvelope;
  /**
   * Advance to `nowMs` and autosave if due. The whole per-frame contract: a
   * focused tab calls it sixty times a second, a throttled tab once a minute,
   * a reopened tab once after two days. All three land on the same state.
   */
  tick(nowMs: number): AdvanceReport;
  /** What an absence WOULD pay, without paying it. For the returning-player summary. */
  preview(nowMs: number): { readonly paidTicks: number; readonly skippedTicks: number };
  /** The player's chosen species, or '' for "the best I have unlocked". */
  setTarget(id: string, nowMs: number): void;
  /** The save as one string the player can copy anywhere. */
  exportText(): string;
  /** Replace this session's run with a pasted string. Never throws. */
  importText(text: unknown, nowMs: number): ImportResult;
  /** Force a write now, regardless of the autosave interval. */
  save(nowMs: number): SaveOutcome;
  /** Wall-clock ms of the last successful write, or -1 if nothing has been written. */
  lastSavedMs(): number;
}

function readText(storage: SaveStorage, key: string): {
  readonly text: string | null;
  readonly note: string;
} {
  try {
    return { text: storage.read(key), note: '' };
  } catch {
    return { text: null, note: 'storage-unreadable' };
  }
}

/**
 * OPEN A SESSION. The single entry point the app calls at startup.
 *
 * `nowMs` is the host's clock at the moment of opening, handed in rather than
 * read, so a test can open a session in 1970 or in 2140 without a shim.
 */
export function openSession(options: SessionOptions): Session {
  const chain =
    options.keys !== undefined && options.keys.length > 0
      ? options.keys
      : options.key !== undefined
        ? [options.key]
        : SAVE_KEYS;
  const key = chain[0] ?? SAVE_KEY;
  const interval =
    options.autosaveIntervalMs !== undefined && options.autosaveIntervalMs >= 0
      ? options.autosaveIntervalMs
      : AUTOSAVE_INTERVAL_MS;
  const storage = options.storage;

  // READ DOWN THE CHAIN. The first slot with anything in it wins; an older slot
  // is reported so a returning player is never silently migrated.
  let read = readText(storage, key);
  const openNotes: string[] = read.note === '' ? [] : [read.note];
  let carriedFrom = '';
  for (const older of chain.slice(1)) {
    if (read.text !== null) break;
    const legacy = readText(storage, older);
    if (legacy.note !== '') openNotes.push(legacy.note);
    if (legacy.text !== null) {
      read = legacy;
      carriedFrom = older;
      openNotes.push(`carried-from:${older}`);
    }
  }

  let envelope: SaveEnvelope;
  if (read.text === null) {
    envelope = createSave(options.seed, options.nowMs);
    openNotes.push('fresh');
  } else {
    // TEXT, so the stored form and the exported form are the SAME form. One
    // format means one parser, one checksum and one migration path; a private
    // storage encoding would be a second save format nobody ever tested.
    const imported = importSave(read.text, options.nowMs);
    if (imported.ok) {
      envelope = imported.save;
      openNotes.push(...imported.notes);
    } else {
      // The stored text is not a save at all. Start fresh rather than refusing
      // to launch, and say why — but do NOT overwrite the unreadable text until
      // something earns a write, so a recoverable paste is still sitting there
      // if the player goes looking for it.
      envelope = createSave(options.seed, options.nowMs);
      openNotes.push(`fresh:${imported.reason}`);
    }
  }

  let lastSaved = -1;

  function write(nowMs: number): SaveOutcome {
    // ALWAYS `key`, the head of the chain, never the slot a legacy save came
    // from. An old build that reads its own slot must find what it left there.
    try {
      storage.write(key, exportSave(envelope));
      lastSaved = nowMs;
      return { written: true, note: '' };
    } catch {
      // A full quota or a locked-down browser. The run keeps playing.
      return { written: false, note: 'storage-unwritable' };
    }
  }

  // A save carried up from an older slot is written into the current one at
  // once, so the migration happens on open rather than on the first autosave a
  // player might never reach.
  if (carriedFrom !== '') write(options.nowMs);

  return {
    openNotes,
    state: () => envelope.sim,
    envelope: () => envelope,

    tick(nowMs: number): AdvanceReport {
      const report = advanceWithReport(envelope.sim, nowMs);
      if (report.state !== envelope.sim) envelope = { ...envelope, sim: report.state };
      if (lastSaved < 0 || nowMs - lastSaved >= interval) write(nowMs);
      return report;
    },

    preview: (nowMs: number) => previewAbsence(envelope.sim, nowMs),

    setTarget(id: string, nowMs: number): void {
      // A deliberate player action, so it is written immediately rather than at
      // the next interval: the one thing worse than losing progress is losing a
      // choice the player is watching the screen make.
      //
      // THROUGH THE SAVE'S OWN VALIDATOR, not a second one. SIM-D6: this used to
      // be a bare length check while save.ts's strField also rejected control
      // characters, so 'cod\t' set the live target and came back as '' on the
      // next reload with no note at either end - the live session and its own
      // save disagreeing about the one field written immediately BECAUSE losing
      // it is worse than losing progress. Two validators for one field is one
      // validator that can drift, and this is what the drift cost.
      const target = strField(id, '', MAX_ID_LENGTH);
      envelope = { ...envelope, sim: { ...envelope.sim, targetSpecies: target } };
      write(nowMs);
    },

    exportText: () => exportSave(envelope),

    importText(text: unknown, nowMs: number): ImportResult {
      const result = importSave(text, nowMs);
      if (!result.ok) return result;
      envelope = result.save;
      write(nowMs);
      return result;
    },

    save: (nowMs: number) => write(nowMs),
    lastSavedMs: () => lastSaved,
  };
}
